// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type SelectChangeEvent } from '@mui/material';
import { type OrderStrategy } from '@shared/data-access/interfaces/redux/portfolio-rebalance-state';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '@advisor-app/store/store';
import {
  selectRebalanceAmount,
  updateWithdrawalOptimization,
} from '@advisor-app/store/slices/parameters';
import { selectTargetPortfolioWeight } from '@shared/data-access/slices/portfolio-positions';

export const useHandlerRebalance = () => {
  const dispatch = useDispatch();
  const investmentSelected = useAppSelector(selectRebalanceAmount);
  const totalWeight = useAppSelector(selectTargetPortfolioWeight);

  const [investment, setInvestment] = useState(investmentSelected);
  const [deviation, setDeviation] = useState(5);
  const [orderStrategy, setOrderStrategy] = useState<OrderStrategy>('FIFO');

  const [isOpen, setIsOpen] = useState(false);

  const handleModal = () => setIsOpen(!isOpen);

  const handleInvestmentChange = (value: number) => setInvestment(value);

  const handleDeviationChange = (value: number) => setDeviation(value);

  const handleOrderStrategyChange = ({ target }: SelectChangeEvent) => {
    setOrderStrategy(target.value as OrderStrategy);
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    dispatch(
      updateWithdrawalOptimization({
        orderStrategy,
        targetError: deviation / 100,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviation, orderStrategy]);

  return {
    handleInvestmentChange,
    handleDeviationChange,
    handleOrderStrategyChange,
    handleModal,
    investment,
    deviation,
    totalWeight,
    isOpen,
    orderStrategy,
  };
};
